/**
 * ClientsListPage
 * Lista todos los clientes (perfiles financieros) del partner.
 * Usa datos reales del endpoint GET /credit/backoffice/profiles.
 * Enriquece con datos de créditos (total otorgado) vía cross-reference.
 * Route: /backoffice/clientes
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { RefreshCw, AlertTriangle, Search, X, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { DataTable } from '@/features/credit/components/DataTable';
import { useBackofficeClients } from '../../hooks';
import { useBackofficeCredits } from '@/features/credit/hooks';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES } from '@/constants';
import { CreditLevel } from '@/types/credit.types';
import { CREDIT_LEVELS } from '@/types/backoffice-crud.types';
import type { CreditProfile } from '@/types/scoring.types';
import { CLIENT_ELIGIBILITY_OPTIONS } from '@/types/clients.types';

// ─── Constants ────────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Clientes', path: ROUTES.CLIENTS_LIST },
];

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getEligibilityVariant(eligible: string): BadgeVariant {
  switch (eligible) {
    case 'YES':
      return 'success';
    case 'NO':
      return 'danger';
    default:
      return 'default';
  }
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function currencyCell(val: number | null | undefined) {
  if (val == null || val === 0) return <span className="text-xs text-muted-foreground">-</span>;
  return <span className="tabular-nums">{formatCurrency(val, 'MXN', 'es-MX')}</span>;
}

// ─── Columns ──────────────────────────────────────────────────────────────────

const columns: ColumnDef<CreditProfile, unknown>[] = [
  {
    accessorKey: 'full_name',
    header: 'Nombre',
    cell: ({ getValue }) => {
      const name = getValue<string | null | undefined>();
      return name ? (
        <span className="font-medium">{name}</span>
      ) : (
        <span className="text-xs text-muted-foreground">-</span>
      );
    },
  },
  {
    accessorKey: 'phone_number',
    header: 'Teléfono',
    cell: ({ getValue }) => (
      <span className="tabular-nums font-medium">{getValue<string>()}</span>
    ),
  },
  {
    accessorKey: 'credit_level',
    header: 'Nivel',
    cell: ({ getValue }) => {
      const level = getValue<string>();
      if (!level) return <span className="text-xs text-muted-foreground">Sin nivel</span>;
      const isValid = Object.values(CreditLevel).includes(level as CreditLevel);
      return isValid ? (
        <CreditLevelBadge level={level as CreditLevel} size="sm" />
      ) : (
        <span className="text-xs text-muted-foreground">{level}</span>
      );
    },
  },
  {
    accessorKey: 'score_total',
    header: 'Score',
    cell: ({ getValue }) => (
      <span className="tabular-nums font-medium">{getValue<number>()}</span>
    ),
  },
  {
    accessorKey: 'is_eligible',
    header: 'Elegible',
    cell: ({ getValue }) => {
      const val = getValue<string>();
      const label = val === 'YES' ? 'Sí' : val === 'NO' ? 'No' : val;
      return <Badge variant={getEligibilityVariant(val)}>{label}</Badge>;
    },
  },
  {
    accessorKey: 'monthly_income_mxn',
    header: 'Ingreso Mensual',
    cell: ({ getValue }) => currencyCell(getValue<number>()),
  },
  {
    accessorKey: 'max_credit_amount_mxn',
    header: 'Monto Máx.',
    cell: ({ getValue }) => currencyCell(getValue<number>()),
  },
  {
    accessorKey: 'total_disbursed_mxn',
    header: 'Total Otorgado',
    cell: ({ getValue }) => currencyCell(getValue<number | null>()),
  },
  {
    accessorKey: 'outstanding_balance_mxn',
    header: 'Deuda Actual',
    cell: ({ getValue }) => currencyCell(getValue<number | null>()),
  },
  {
    accessorKey: 'overdue_balance_mxn',
    header: 'Deuda Morosa',
    cell: ({ getValue }) => {
      const val = getValue<number | null>();
      if (val == null || val === 0) return <span className="text-xs text-muted-foreground">-</span>;
      return <span className="tabular-nums text-red-600 dark:text-red-400 font-medium">{formatCurrency(val, 'MXN', 'es-MX')}</span>;
    },
  },
  {
    accessorKey: 'calculation_date',
    header: 'Calculado',
    cell: ({ getValue }) => formatDateShort(getValue<string>()),
  },
];

// ─── Skeleton (solo para el body de la tabla, los filtros se mantienen visibles) ───

function TableBodySkeleton() {
  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={`row-skeleton-${i.toString()}`} variant="rectangular" height={44} />
      ))}
    </div>
  );
}

// ─── Error State ──────────────────────────────────────────────────────────────

function ListError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
      <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold text-foreground">
        Error al cargar los clientes
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Hubo un error al obtener los datos. Intenta de nuevo.
      </p>
      <Button className="mt-4" onClick={onRetry}>
        <RefreshCw size={16} />
        Reintentar
      </Button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ClientsListPage() {
  const navigate = useNavigate();

  const [levelFilter, setLevelFilter] = useState('');
  const [eligibilityFilter, setEligibilityFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { clients, pagination, isLoading, isError, refetch } = useBackofficeClients({
    ...(levelFilter ? { credit_level: levelFilter } : {}),
    ...(eligibilityFilter ? { is_eligible: eligibilityFilter } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  });
  const { credits } = useBackofficeCredits();

  // Cross-reference: computar total otorgado por directoryId desde la lista de créditos
  const enrichedClients = useMemo(() => {
    // Agrupar créditos por directory_id
    const creditsByDir = new Map<string, { totalDisbursed: number }>();
    for (const c of credits) {
      const existing = creditsByDir.get(c.directory_id);
      if (existing) {
        existing.totalDisbursed += c.amount_mxn;
      } else {
        creditsByDir.set(c.directory_id, { totalDisbursed: c.amount_mxn });
      }
    }

    return clients.map((client) => {
      const creditData = creditsByDir.get(client.directory_id);
      return {
        ...client,
        // Usar valor del backend si existe (>0), sino computar de cross-reference
        total_disbursed_mxn:
          client.total_disbursed_mxn || creditData?.totalDisbursed || 0,
      };
    });
  }, [clients, credits]);

  // Filtros (level/eligibility/search) van al backend via useBackofficeClients.
  const filteredClients = enrichedClients;

  const levelOptions = [
    { value: '', label: 'Todos los niveles' },
    ...CREDIT_LEVELS.map((l) => ({ value: l, label: l })),
  ];

  const eligibilityOptions = CLIENT_ELIGIBILITY_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
  }));

  const handleRowClick = (row: CreditProfile) => {
    navigate(ROUTES.EXPEDIENTE_DETAIL.replace(':directoryId', row.directory_id));
  };

  // Spinner sutil mientras debounce está corriendo o el query está fetching.
  // No esconde el input — solo informa que algo está pasando.
  const isPendingSearch = searchInput !== '' && searchInput !== debouncedSearch;
  const showSpinner = isPendingSearch || isLoading;

  return (
    <div>
      <PageHeader
        title="Clientes"
        subtitle={isLoading && pagination.total_items === 0
          ? 'Listado de clientes del partner'
          : `${pagination.total_items} clientes registrados`}
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              Actualizar
            </Button>
          </div>
        }
      />

      {/* Filtros — siempre montados para no perder el foco del input al refetchar */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="relative min-w-[280px] flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            placeholder="Buscar por nombre, teléfono o ID..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            // style inline para ganar specificity sobre el padding default del Input.module.css
            // (sin esto el texto se encima con el icono de la lupita a la izquierda).
            style={{ paddingLeft: '2.25rem', paddingRight: '2.25rem' }}
            aria-label="Buscar clientes"
            autoFocus
          />
          {/* Botón limpiar o spinner — nunca conviven, el spinner tiene prioridad cuando hay search */}
          {showSpinner && searchInput ? (
            <Loader2 size={14} className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
          ) : searchInput ? (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
              aria-label="Limpiar búsqueda"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
        <Select
          options={levelOptions}
          value={levelFilter}
          onChange={setLevelFilter}
          placeholder="Filtrar por nivel"
        />
        <Select
          options={eligibilityOptions}
          value={eligibilityFilter}
          onChange={setEligibilityFilter}
          placeholder="Filtrar por elegibilidad"
        />
      </div>

      {/* Contenido — solo este bloque cambia entre loading/error/tabla. Los filtros de arriba se mantienen montados. */}
      {isError ? (
        <ListError onRetry={() => refetch()} />
      ) : isLoading && filteredClients.length === 0 ? (
        <TableBodySkeleton />
      ) : (
        <DataTable
          data={filteredClients}
          columns={columns}
          hideSearch
          onRowClick={handleRowClick}
          emptyMessage={debouncedSearch ? `Sin resultados para "${debouncedSearch}"` : 'No se encontraron clientes'}
          pageSize={10}
        />
      )}
    </div>
  );
}
